import type {
  RegistryAllocation,
  RegistryEntityDetail,
  RegistryEntitySummary,
  RegistryFundOpportunityAllocations,
} from '../api/registryEntityTypes';

/**
 * Where the console reads platform entities and allocations from: the admin API when signed in,
 * or the prototype's mock data in the same shapes when REACT_APP_REGISTRY_MOCK=true.
 */
export interface EntitySource {
  /** True for the mock data, so screens can say it is sample data. */
  readonly sample?: boolean;
  listEntities(): Promise<RegistryEntitySummary[]>;
  getEntity(entityId: string): Promise<RegistryEntityDetail>;
  listFundOpportunities(): Promise<RegistryFundOpportunityAllocations[]>;
  listFundOpportunityAllocations(fundOpportunityId: string): Promise<RegistryAllocation[]>;
}

/** An allocation's id: the investor and entity external ids, which are unique together. */
export function allocationKey(
  allocation: Pick<RegistryAllocation, 'investorExternalId' | 'investmentEntityExternalId'>,
): string {
  return `${allocation.investorExternalId}:${allocation.investmentEntityExternalId}`;
}

/**
 * Adds decimal-string amounts as whole cents. Integer arithmetic is exact up to
 * Number.MAX_SAFE_INTEGER cents (about $90 trillion), so no float rounding creeps in.
 */
export function sumAmounts(amounts: string[]): string {
  const cents = amounts.reduce((total, amount) => total + toCents(amount), 0);
  const digits = String(Math.abs(cents)).padStart(3, '0');
  return `${cents < 0 ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

function toCents(amount: string): number {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  if (!match) throw new Error(`Not a money amount: ${amount}`);
  const cents = Number(match[2]) * 100 + Number((match[3] ?? '').padEnd(2, '0'));
  return match[1] ? -cents : cents;
}

export function summariseEntity(entity: RegistryEntityDetail): RegistryEntitySummary {
  return {
    externalId: entity.externalId,
    name: entity.name,
    type: entity.type,
    abn: entity.abn,
    acn: entity.acn,
    isForeignInvestor: entity.isForeignInvestor,
    ownerCount: entity.contacts.filter((contact) => contact.role === 'OWNER').length,
    representativeCount: entity.contacts.filter((contact) => contact.role === 'REPRESENTATIVE')
      .length,
    allocationCount: entity.allocations.length,
    totalAllocated: sumAmounts(entity.allocations.map((allocation) => allocation.allocatedAmount)),
    createdAt: entity.createdAt,
  };
}
